import { UseQueryOptions } from '@tanstack/react-query';
import {
	OAuthConnectionConfig,
	OAuthConnectionTenant,
	XeroInitiateOAuthPayload,
	XeroWebAppConnectionPayload,
	xeroConnectionControllerPath,
	xeroConnectionGetScopedConnectionsPath,
	xeroDisconnectConnectionPath,
	xeroGetTenantsPath,
	xeroOAuthInitiatePath,
} from 'ihub/common';
import { CRUDHelperBase } from '../classes/crud-helper-base';
import {
	buildURL,
	getTenantID,
	standardGet,
	standardPost,
	validateOkResponse,
} from '../classes/http-helper-const';
import Parameter from '../classes/query-parameter';
import { cacheStaleTime } from '../constants';
import QueryClientWrapper from '../utils/create-query-client';

export class XeroConnectionHttpService extends CRUDHelperBase<OAuthConnectionConfig> {
	constructor() {
		super(xeroConnectionControllerPath);
	}

	getScopedConnectionsQuery(
		queryParams?: Parameter[]
	): UseQueryOptions<OAuthConnectionConfig[], Error, OAuthConnectionConfig[]> {
		return {
			queryKey: [
				getTenantID(),
				this.objectType,
				xeroConnectionGetScopedConnectionsPath,
				queryParams?.map(x => `${x.key}=${x.value}`).join('&'),
			],
			queryFn: async (): Promise<OAuthConnectionConfig[]> => {
				const url = buildURL(
					this.objectType,
					[xeroConnectionGetScopedConnectionsPath],
					queryParams
				);
				const res = await standardGet(url);
				await validateOkResponse(res, 'get scoped connections', this.objectType);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
		};
	}

	async initiateOAuth(payload: XeroInitiateOAuthPayload): Promise<{ consentUrl: string }> {
		const url = buildURL(this.objectType, [xeroOAuthInitiatePath]);
		const res = await standardPost(url, payload);
		await validateOkResponse(res, 'initiate OAuth', this.objectType);
		return await res.clone().json();
	}

	async getTenants(payload: XeroWebAppConnectionPayload): Promise<OAuthConnectionTenant[]> {
		const url = buildURL(this.objectType, [xeroGetTenantsPath]);
		const res = await standardPost(url, payload);
		await validateOkResponse(res, 'get tenants', this.objectType);
		return await res.clone().json();
	}

	async disconnectConnection(payload: XeroWebAppConnectionPayload): Promise<boolean> {
		const url = buildURL(this.objectType, [xeroDisconnectConnectionPath]);
		const res = await standardPost(url, payload);
		await validateOkResponse(res, 'disconnect connection', this.objectType);
		return await res.clone().json();
	}

	async invalidateGetScopedConnections(remove = false) {
		await QueryClientWrapper.InvalidateKey(
			[getTenantID(), this.objectType, xeroConnectionGetScopedConnectionsPath],
			remove
		);
	}
}
